import { TintableLayerData } from './tintable-layer.data';
import { LayerData, LAYER_TYPE } from './layer.data';
import { UserImageData } from 'src/app/data/user/user-image.data';
import { LabelEvent } from 'src/app/events/label.event';
import { ContextData } from 'src/app/data/context.data';
import { CoordUtil } from 'src/app/tools/coord.utils';
import { Rectangle } from 'src/app/tools/rectangle';

export class ImageLayerData extends TintableLayerData {

  private _imageData:UserImageData;
  private _crop:any;
  private _normalizedCrop:Rectangle;

  private _croppedImg_x:number;
  private _croppedImg_y:number;
  private _croppedImg_width:number;
  private _croppedImg_height:number;

  /**
   * Contructor
   */
  constructor(imageData: UserImageData, crop: any, color: string, x:number = 0, y:number = 0, width:number = NaN, height:number = NaN, rotation:number = 0, alpha:number = 1, locks:any = null, source:string = "CreationLayer", special:string = null) {
    super(color, x, y, width, height, rotation, alpha, locks, source, special);



    this._imageData = imageData;
    this.type = LayerData.IMAGE;
    if (!crop) {
      this._crop = {x:0, y:0, width:this._imageData.width, height:this._imageData.height};
    } else {
      this._crop = crop;
    }

    this._computeNormalizedCrop();

    if(width != NaN){
      this._croppedImg_x = this._x;
      this._croppedImg_y = this._y;
      this._croppedImg_width = this._width;
      this._croppedImg_height = this._height;
      this._applyCroppedProperties();
    }
  }

  private _applyCroppedProperties(){
    let _maskPos = CoordUtil.rotate2D({x:this._normalizedCrop.x,y:this._normalizedCrop.y},this._rotation);
    this._x = this._croppedImg_x + _maskPos.x * this._croppedImg_width;
    this._y = this._croppedImg_y - _maskPos.y * this._croppedImg_height;
    this._width = this._croppedImg_width * this._normalizedCrop.width;
    this._height = this._croppedImg_height * this._normalizedCrop.height;
  }

  private _resetBeforeCropImageData(){
    let _parentOrigin = CoordUtil.rotate2D({x:-this._normalizedCrop.x*(this.width/this._normalizedCrop.width),y:this._normalizedCrop.y*(this.height/this._normalizedCrop.height)},-this.rotation);

    let _beforeCropImageRect = new Rectangle(
      this.x + _parentOrigin.x,
      this.y - _parentOrigin.y,
      (this.width/this._normalizedCrop.width),
      (this.height/this._normalizedCrop.height)
    );

    this.beforeCropImageRect = _beforeCropImageRect;
  }


  /**
   * x
   */
  public get x():number {
    return this._x * ContextData.MM_TO_PIXELS;
  }

  public set x(value:number) {
    this._x = value / ContextData.MM_TO_PIXELS;
    this._resetBeforeCropImageData();
  }

  /**
   * y
   */
  public get y():number {
    return this._y * ContextData.MM_TO_PIXELS;
  }

  public set y(value:number) {
    this._y = value / ContextData.MM_TO_PIXELS;
    this._resetBeforeCropImageData();
  }

  public invalidate() {
    this._resetBeforeCropImageData();
    super.invalidate();
  }


  /**
   * Image data
   */
  public get imageData():UserImageData {
    return this._imageData;
  }

  /**
   * Crop
   */
  public get crop():any {
    return this._crop;
  }

  private _computeNormalizedCrop(){
    this._normalizedCrop = new Rectangle(
      this._crop.x/this._imageData.width,
      this._crop.y/this._imageData.height,
      this._crop.width/this._imageData.width,
      this._crop.height/this._imageData.height,
    );
  }

  public set crop(value:any) {
    this._crop = value;
    this._computeNormalizedCrop();
    this.onChange.emit(new LabelEvent(LabelEvent.UPDATE_LAYER, this));
  }
  /**
   * Clone
   */
  public clone(keepData:boolean = false):LayerData {
    var layerData:ImageLayerData = new ImageLayerData(this._imageData, this._crop, this._color);
    layerData.x = this.x;
    layerData.y = this.y;
    layerData.width = this.width;
    layerData.height = this.height;
    layerData.rotation = this.rotation;
    layerData.alpha = this.alpha;
    layerData.crop = {x:this.crop.x,y:this.crop.y,width:this.crop.width,height:this.crop.height};
    layerData.beforeCropImageRect = this.beforeCropImageRect;
    layerData.locks = keepData ? {position:this._locks.position, rotation:this._locks.rotation, size:this._locks.size, deletion:this._locks.deletion, depth:this._locks.depth, crop:this._locks.crop, tint:this._locks.tint} : null;
    layerData.source = this.source;
    layerData.special = this.special;
    layerData.index = keepData ? this._index : -1;

    return layerData;
  }

  /**
	 * Get data
	 */
	public getData(): any {
    var returnData: any = {

    };

    if (this.special == 'background') {
      returnData = {
        type: LAYER_TYPE.BACKGROUND,
        src: this._imageData.url,
        design: this.source == LayerData.DESIGN ? 1 : 0,
        properties: {
          type: this.locks.tint ? 2 : 1,
          x: this._x,
          y: this._y,
          width: this._width,
          height: this._height,
          rotation: this._rotation,
        },
        locking: {
          position: this._locks.position,
          rotation: this._locks.rotation,
          size: this._locks.size,
          delete: this._locks.delete
        }
      };

      if (this._color) {
        returnData.properties.color = this._color;
      }
    } else {
      returnData = {
        type: LAYER_TYPE.PICTURE,
        src: this._imageData.url,
        design: this.source == LayerData.DESIGN ? 1 : 0,
        crop:{
          x:this._crop.x,
          y:this._crop.y,
          width:this._crop.width,
          height:this._crop.height
        },
        properties: {
          x: this._x,
          y: this._y,
          width: this._width,
          height: this._height,
          rotation: this._rotation,
        },
        locking: {
          position: this._locks.position,
          rotation: this._locks.rotation,
          size: this._locks.size,
          delete: this._locks.delete
        }
      };

      // add crop.

    }


		return returnData;
  }


  public get beforeCropImageRect():Rectangle {
    return new Rectangle(
      this._croppedImg_x* ContextData.MM_TO_PIXELS,
      this._croppedImg_y* ContextData.MM_TO_PIXELS,
      this._croppedImg_width* ContextData.MM_TO_PIXELS,
      this._croppedImg_height* ContextData.MM_TO_PIXELS,
    );
  }

  public set beforeCropImageRect(value:Rectangle) {
    //console.log(value);
      this._croppedImg_x = value.x / ContextData.MM_TO_PIXELS;
      this._croppedImg_y = value.y/ ContextData.MM_TO_PIXELS;
      this._croppedImg_width = value.width/ ContextData.MM_TO_PIXELS;
      this._croppedImg_height = value.height/ ContextData.MM_TO_PIXELS;
  }

  public get normalisedCropRect():Rectangle {
    return this._normalizedCrop;
  }


  /**
   * Cleanup
   */
  public cleanup():void {
    this._imageData = null;
    this._crop = null;
    this._normalizedCrop = null;
    super.cleanup();
  }

}
